import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft,
  Plus,
  Trash,
  FilePdf,
  Users,
  FileText,
  ShieldCheck,
  Certificate,
  SpinnerGap,
  CheckCircle,
  Warning,
  EnvelopeSimple,
  Lock,
  User,
  AirplaneTilt,
  Globe,
  Bank,
  Key
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const TemplateSection = ({ templates, loading, uploading, templateName, setTemplateName, endpoint, setTemplates, setUploading, uploadInputId, onUpload, onDelete }) => (
  <div className="space-y-4">
    <div className="border border-dashed border-[#333] bg-[#0A0A0A] p-4">
      <div className="flex flex-col sm:flex-row gap-3 items-end">
        <div className="flex-1 space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Название шаблона</Label>
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Например: ДИЧЕЛ, Милка, Найк..."
            className="bg-[#141414] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-10 focus:border-[#FACC15] focus:ring-0"
            data-testid={`${uploadInputId}-name-input`}
          />
        </div>
        <label className="cursor-pointer shrink-0">
          <input
            type="file"
            accept=".pdf"
            className="hidden"
            data-testid={`${uploadInputId}-file-input`}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) onUpload(file, templateName, endpoint, setUploading, setTemplates, setTemplateName);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            className="border-[#FACC15] bg-transparent text-[#FACC15] hover:bg-[#FACC15]/10 rounded-none h-10 px-6"
            disabled={uploading || !templateName.trim()}
            asChild
          >
            <span>
              {uploading ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
              Загрузить PDF
            </span>
          </Button>
        </label>
      </div>
    </div>
    {loading ? (
      <div className="flex justify-center py-8">
        <SpinnerGap size={32} className="animate-spin text-[#A3A3A3]" />
      </div>
    ) : templates.length === 0 ? (
      <div className="text-center py-8 text-[#525252]">
        <FilePdf size={48} className="mx-auto mb-3 opacity-30" />
        <p className="text-sm">Нет загруженных шаблонов</p>
      </div>
    ) : (
      <div className="space-y-2">
        {templates.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-3 border border-[#262626] bg-[#141414] hover:border-[#333] transition-colors" data-testid={`template-item-${t.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <FilePdf size={22} className="text-[#FACC15] shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#FAFAFA] truncate">{t.name}</p>
                <p className="text-xs text-[#525252] truncate">{t.filename} | {new Date(t.uploaded_at).toLocaleDateString('ru-RU')}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(t.id, endpoint, setTemplates)}
              className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-none h-8 w-8 p-0 shrink-0"
              data-testid={`delete-template-${t.id}`}
            >
              <Trash size={16} />
            </Button>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('security');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Access Key
  const [oldKey, setOldKey] = useState('');
  const [newKey, setNewKey] = useState('');
  const [savingKey, setSavingKey] = useState(false);

  // Admin 2FA
  const [admin2faEnabled, setAdmin2faEnabled] = useState(false);
  const [admin2faSecret, setAdmin2faSecret] = useState('');
  const [admin2faLoading, setAdmin2faLoading] = useState(true);

  // Managers
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [showAddManager, setShowAddManager] = useState(false);
  const [managerForm, setManagerForm] = useState({ email: '', password: '', name: '' });
  const [addingManager, setAddingManager] = useState(false);
  const [newManagerTotp, setNewManagerTotp] = useState(null); // {name, email, secret}

  // Contract templates
  const [contractTemplates, setContractTemplates] = useState([]);
  const [loadingContractTemplates, setLoadingContractTemplates] = useState(true);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [contractTemplateName, setContractTemplateName] = useState('');

  // Verification templates
  const [verificationTemplates, setVerificationTemplates] = useState([]);
  const [loadingVerificationTemplates, setLoadingVerificationTemplates] = useState(true);
  const [uploadingVerification, setUploadingVerification] = useState(false);
  const [verificationTemplateName, setVerificationTemplateName] = useState('');

  // Employment templates
  const [employmentTemplates, setEmploymentTemplates] = useState([]);
  const [loadingEmploymentTemplates, setLoadingEmploymentTemplates] = useState(true);
  const [uploadingEmployment, setUploadingEmployment] = useState(false);
  const [employmentTemplateName, setEmploymentTemplateName] = useState('');

  // Invitation templates
  const [invitationTemplates, setInvitationTemplates] = useState([]);
  const [loadingInvitationTemplates, setLoadingInvitationTemplates] = useState(true);
  const [uploadingInvitation, setUploadingInvitation] = useState(false);
  const [invitationTemplateName, setInvitationTemplateName] = useState('');

  // Visa templates
  const [visaTemplates, setVisaTemplates] = useState([]);
  const [loadingVisaTemplates, setLoadingVisaTemplates] = useState(true);
  const [uploadingVisa, setUploadingVisa] = useState(false);
  const [visaTemplateName, setVisaTemplateName] = useState('');

  // Statement templates
  const [statementTemplates, setStatementTemplates] = useState([]);
  const [loadingStatementTemplates, setLoadingStatementTemplates] = useState(true);
  const [uploadingStatement, setUploadingStatement] = useState(false);
  const [statementTemplateName, setStatementTemplateName] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchManagers();
    fetchAdmin2fa();
    fetchContractTemplates();
    fetchVerificationTemplates();
    fetchEmploymentTemplates();
    fetchInvitationTemplates();
    fetchVisaTemplates();
    fetchStatementTemplates();
  }, [user, navigate]);

  const showSuccess = (msg) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); };
  const showError = (msg) => { setErrorMessage(msg); setTimeout(() => setErrorMessage(''), 5000); };

  // ===== ACCESS KEY =====
  const handleSaveKey = async () => {
    if (!oldKey.trim()) {
      showError('Введите текущий ключ');
      return;
    }
    if (!newKey.trim() || newKey.trim().length < 6) {
      showError('Новый ключ должен быть минимум 6 символов');
      return;
    }
    setSavingKey(true);
    try {
      await axios.put(`${API}/api/admin/access-key`, { old_key: oldKey.trim(), new_key: newKey.trim() });
      showSuccess('Ключ доступа обновлён!');
      setOldKey('');
      setNewKey('');
    } catch (e) {
      showError(e.response?.data?.detail || 'Ошибка сохранения ключа');
    } finally { setSavingKey(false); }
  };

  // ===== ADMIN 2FA =====
  const fetchAdmin2fa = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/my-totp`);
      setAdmin2faEnabled(res.data.totp_enabled || false);
      setAdmin2faSecret(res.data.totp_secret || '');
    } catch (e) { console.error(e); }
    finally { setAdmin2faLoading(false); }
  };

  const handleToggleAdmin2fa = async (enabled) => {
    try {
      const res = await axios.put(`${API}/api/admin/users/${user.id}/toggle-2fa`, { enabled });
      setAdmin2faEnabled(enabled);
      if (res.data.totp_secret) setAdmin2faSecret(res.data.totp_secret);
      showSuccess(enabled ? '2FA включена' : '2FA отключена');
    } catch (e) {
      showError(e.response?.data?.detail || 'Ошибка переключения 2FA');
    }
  };

  const handleToggleManager2fa = async (managerId, enabled) => {
    try {
      const res = await axios.put(`${API}/api/admin/users/${managerId}/toggle-2fa`, { enabled });
      setManagers(prev => prev.map(m => m.id === managerId
        ? { ...m, totp_enabled: enabled, totp_secret: res.data.totp_secret || m.totp_secret }
        : m
      ));
      showSuccess(enabled ? '2FA включена' : '2FA отключена');
    } catch (e) {
      showError(e.response?.data?.detail || 'Ошибка переключения 2FA');
    }
  };

  // ===== MANAGERS =====
  const fetchManagers = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/managers`);
      setManagers(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingManagers(false); }
  };

  const handleAddManager = async () => {
    if (!managerForm.email || !managerForm.password || !managerForm.name) {
      showError('Заполните все поля');
      return;
    }
    setAddingManager(true);
    try {
      const res = await axios.post(`${API}/api/admin/managers`, managerForm);
      setManagers(prev => [...prev, res.data]);
      setShowAddManager(false);
      setManagerForm({ email: '', password: '', name: '' });
      // Show TOTP secret for the new manager
      const secret = res.data.totp_secret;
      if (secret) {
        setNewManagerTotp({ name: res.data.name, email: res.data.email, secret });
      } else {
        showSuccess('Менеджер создан!');
      }
    } catch (e) {
      showError(e.response?.data?.detail || 'Ошибка создания менеджера');
    } finally { setAddingManager(false); }
  };

  const handleDeleteManager = async (id) => {
    try {
      await axios.delete(`${API}/api/admin/managers/${id}`);
      setManagers(prev => prev.filter(m => m.id !== id));
      showSuccess('Менеджер удален');
    } catch (e) { showError('Ошибка удаления'); }
  };

  // ===== TEMPLATE UPLOAD HELPERS =====
  const uploadTemplate = async (file, name, endpoint, setUploading, setTemplates, setName) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showError('Только PDF файлы');
      return;
    }
    if (!name.trim()) {
      showError('Введите название шаблона');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name.trim());
    try {
      const res = await axios.post(`${API}/api/${endpoint}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTemplates(prev => [...prev, res.data]);
      setName('');
      showSuccess('Шаблон загружен!');
    } catch (e) {
      showError(e.response?.data?.detail || 'Ошибка загрузки');
    } finally { setUploading(false); }
  };

  const deleteTemplate = async (id, endpoint, setTemplates) => {
    try {
      await axios.delete(`${API}/api/${endpoint}/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      showSuccess('Шаблон удален');
    } catch (e) { showError('Ошибка удаления'); }
  };

  // ===== FETCH TEMPLATES =====
  const fetchContractTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/templates`);
      setContractTemplates(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingContractTemplates(false); }
  };

  const fetchVerificationTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/verification-templates`);
      setVerificationTemplates(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingVerificationTemplates(false); }
  };

  const fetchEmploymentTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/employment-templates`);
      setEmploymentTemplates(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingEmploymentTemplates(false); }
  };

  const fetchInvitationTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/invitation-templates`);
      setInvitationTemplates(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingInvitationTemplates(false); }
  };

  const fetchVisaTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/visa-templates`);
      setVisaTemplates(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingVisaTemplates(false); }
  };

  const fetchStatementTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/statement-templates`);
      setStatementTemplates(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingStatementTemplates(false); }
  };

  const sections = [
    { id: 'security', label: 'Безопасность', icon: Key },
    { id: 'managers', label: 'Менеджеры', icon: Users },
    { id: 'contracts', label: 'Шаблоны контрактов', icon: FileText },
    { id: 'verifications', label: 'Шаблоны верификации', icon: ShieldCheck },
    { id: 'employment', label: 'Шаблоны трудовых договоров', icon: Certificate },
    { id: 'invitations', label: 'Шаблоны приглашений', icon: AirplaneTilt },
    { id: 'visas', label: 'Шаблоны виз', icon: Globe },
    { id: 'statements', label: 'Шаблоны выписок', icon: Bank },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="settings-page">
      {/* Header */}
      <header className="border-b border-[#262626] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#262626] rounded-none"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft size={18} className="mr-2" />
                Назад
              </Button>
              <h1 className="text-2xl font-black tracking-tight text-[#FAFAFA]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                НАСТРОЙКИ
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Notifications */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/50 text-green-400 animate-fade-in" data-testid="success-message">
          <CheckCircle size={20} weight="bold" />
          <span className="text-sm">{successMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/50 text-red-400 animate-fade-in" data-testid="error-message">
          <Warning size={20} weight="bold" />
          <span className="text-sm">{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="ml-2 hover:text-red-300">x</button>
        </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {sections.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors ${
                      activeSection === s.id
                        ? 'bg-[#FACC15]/10 text-[#FACC15] border-l-2 border-[#FACC15]'
                        : 'text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#141414] border-l-2 border-transparent'
                    }`}
                    data-testid={`settings-nav-${s.id}`}
                  >
                    <Icon size={18} />
                    {s.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main area */}
          <div className="lg:col-span-3">
            {/* SECURITY */}
            {activeSection === 'security' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Ключ доступа</h2>
                  <p className="text-xs text-[#525252] mt-1">Смена ключа доступа. Ключ хранится в зашифрованном виде (bcrypt)</p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Текущий ключ</Label>
                    <Input
                      type="password"
                      value={oldKey}
                      onChange={(e) => setOldKey(e.target.value)}
                      placeholder="Введите текущий ключ"
                      className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-10 focus:border-[#FACC15] focus:ring-0"
                      data-testid="old-key-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Новый ключ</Label>
                    <Input
                      type="password"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      placeholder="Введите новый ключ (мин. 6 символов)"
                      className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-10 focus:border-[#FACC15] focus:ring-0"
                      data-testid="new-key-input"
                    />
                  </div>
                  <Button
                    onClick={handleSaveKey}
                    disabled={savingKey || !oldKey.trim() || !newKey.trim()}
                    className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none h-10 px-6"
                    data-testid="save-access-key-btn"
                  >
                    {savingKey ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <Lock size={16} className="mr-2" />}
                    Сменить ключ
                  </Button>
                  <div className="mt-4 p-3 border border-[#262626] bg-[#0A0A0A]">
                    <p className="text-xs text-[#525252] leading-relaxed">
                      Ключ хранится как bcrypt-хеш. Через API невозможно получить текущий ключ. Для смены необходимо знать текущий ключ. После 5 неудачных попыток ввода ключа — блокировка на 15 минут.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* SECURITY — 2FA */}
            {activeSection === 'security' && (
              <div className="border border-[#262626] bg-[#141414] mt-4">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Двухфакторная аутентификация (2FA)</h2>
                  <p className="text-xs text-[#525252] mt-1">Google Authenticator для дополнительной защиты</p>
                </div>
                <div className="p-4 space-y-4">
                  {admin2faLoading ? (
                    <div className="flex justify-center py-4"><SpinnerGap size={24} className="animate-spin text-[#A3A3A3]" /></div>
                  ) : (
                    <>
                      {/* Admin's own 2FA */}
                      <div className="flex items-center justify-between p-3 border border-[#262626]">
                        <div>
                          <p className="text-sm font-medium text-[#FAFAFA]">Моя 2FA (Админ)</p>
                          <p className="text-xs text-[#525252]">{admin2faEnabled ? 'Включена' : 'Отключена'}</p>
                        </div>
                        <Switch
                          checked={admin2faEnabled}
                          onCheckedChange={handleToggleAdmin2fa}
                          data-testid="admin-2fa-toggle"
                        />
                      </div>
                      {admin2faEnabled && admin2faSecret && (
                        <div className="p-3 border border-[#FACC15]/30 bg-[#FACC15]/5">
                          <p className="text-xs text-[#FACC15] mb-1 font-semibold uppercase tracking-wider">TOTP Секрет (Google Authenticator)</p>
                          <p className="text-sm font-mono select-all text-[#FAFAFA] break-all" data-testid="admin-totp-secret">{admin2faSecret}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* MANAGERS */}
            {activeSection === 'managers' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626] flex justify-between items-center">
                  <div>
                    <h2 className="text-base font-semibold text-[#FAFAFA]">Менеджеры</h2>
                    <p className="text-xs text-[#525252] mt-1">Создайте аккаунты для менеджеров</p>
                  </div>
                  <Button
                    onClick={() => setShowAddManager(true)}
                    className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none h-9 text-xs"
                    data-testid="add-manager-btn"
                  >
                    <Plus size={14} className="mr-2" />
                    Добавить
                  </Button>
                </div>
                <div className="p-4">
                  {loadingManagers ? (
                    <div className="flex justify-center py-8"><SpinnerGap size={32} className="animate-spin text-[#A3A3A3]" /></div>
                  ) : managers.length === 0 ? (
                    <div className="text-center py-8 text-[#525252]">
                      <Users size={48} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Нет менеджеров</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {managers.map((m) => (
                        <div key={m.id} className="flex items-center justify-between p-3 border border-[#262626] hover:border-[#333] transition-colors" data-testid={`manager-item-${m.id}`}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 bg-[#FACC15]/10 flex items-center justify-center shrink-0">
                              <User size={16} className="text-[#FACC15]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-[#FAFAFA]">{m.name}</p>
                              <p className="text-xs text-[#525252]">{m.email}</p>
                              {m.totp_enabled && m.totp_secret && (
                                <p className="text-xs text-[#A3A3A3] mt-1 font-mono">
                                  2FA: <span className="select-all text-[#FACC15]">{m.totp_secret}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[#525252]">2FA</span>
                              <Switch
                                checked={m.totp_enabled || false}
                                onCheckedChange={(val) => handleToggleManager2fa(m.id, val)}
                                data-testid={`manager-2fa-toggle-${m.id}`}
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteManager(m.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-none h-8 w-8 p-0"
                              data-testid={`delete-manager-${m.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CONTRACT TEMPLATES */}
            {activeSection === 'contracts' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Шаблоны контрактов</h2>
                  <p className="text-xs text-[#525252] mt-1">Загрузите PDF шаблоны для генерации контрактов</p>
                </div>
                <div className="p-4">
                  <TemplateSection
                    templates={contractTemplates}
                    loading={loadingContractTemplates}
                    uploading={uploadingContract}
                    templateName={contractTemplateName}
                    setTemplateName={setContractTemplateName}
                    endpoint="templates"
                    setTemplates={setContractTemplates}
                    setUploading={setUploadingContract}
                    uploadInputId="contract-template"
                    onUpload={uploadTemplate}
                    onDelete={deleteTemplate}
                  />
                </div>
              </div>
            )}

            {/* VERIFICATION TEMPLATES */}
            {activeSection === 'verifications' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Шаблоны верификации</h2>
                  <p className="text-xs text-[#525252] mt-1">Загрузите PDF шаблоны для верификации</p>
                </div>
                <div className="p-4">
                  <TemplateSection
                    templates={verificationTemplates}
                    loading={loadingVerificationTemplates}
                    uploading={uploadingVerification}
                    templateName={verificationTemplateName}
                    setTemplateName={setVerificationTemplateName}
                    endpoint="verification-templates"
                    setTemplates={setVerificationTemplates}
                    setUploading={setUploadingVerification}
                    uploadInputId="verification-template"
                    onUpload={uploadTemplate}
                    onDelete={deleteTemplate}
                  />
                </div>
              </div>
            )}

            {/* EMPLOYMENT TEMPLATES */}
            {activeSection === 'employment' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Шаблоны трудовых договоров</h2>
                  <p className="text-xs text-[#525252] mt-1">Загрузите PDF шаблоны трудовых договоров (ДИЧЕЛ, Милка, Найк и т.д.)</p>
                </div>
                <div className="p-4">
                  <TemplateSection
                    templates={employmentTemplates}
                    loading={loadingEmploymentTemplates}
                    uploading={uploadingEmployment}
                    templateName={employmentTemplateName}
                    setTemplateName={setEmploymentTemplateName}
                    endpoint="employment-templates"
                    setTemplates={setEmploymentTemplates}
                    setUploading={setUploadingEmployment}
                    uploadInputId="employment-template"
                    onUpload={uploadTemplate}
                    onDelete={deleteTemplate}
                  />
                </div>
              </div>
            )}

            {/* INVITATION TEMPLATES */}
            {activeSection === 'invitations' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Шаблоны приглашений</h2>
                  <p className="text-xs text-[#525252] mt-1">Загрузите PDF шаблоны приглашений (Германия, Англия и т.д.)</p>
                </div>
                <div className="p-4">
                  <TemplateSection
                    templates={invitationTemplates}
                    loading={loadingInvitationTemplates}
                    uploading={uploadingInvitation}
                    templateName={invitationTemplateName}
                    setTemplateName={setInvitationTemplateName}
                    endpoint="invitation-templates"
                    setTemplates={setInvitationTemplates}
                    setUploading={setUploadingInvitation}
                    uploadInputId="invitation-template"
                    onUpload={uploadTemplate}
                    onDelete={deleteTemplate}
                  />
                </div>
              </div>
            )}

            {/* VISA TEMPLATES */}
            {activeSection === 'visas' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Шаблоны виз</h2>
                  <p className="text-xs text-[#525252] mt-1">Загрузите PDF шаблоны визовых анкет (Германия, Англия и т.д.)</p>
                </div>
                <div className="p-4">
                  <TemplateSection
                    templates={visaTemplates}
                    loading={loadingVisaTemplates}
                    uploading={uploadingVisa}
                    templateName={visaTemplateName}
                    setTemplateName={setVisaTemplateName}
                    endpoint="visa-templates"
                    setTemplates={setVisaTemplates}
                    setUploading={setUploadingVisa}
                    uploadInputId="visa-template"
                    onUpload={uploadTemplate}
                    onDelete={deleteTemplate}
                  />
                </div>
              </div>
            )}

            {/* STATEMENT TEMPLATES */}
            {activeSection === 'statements' && (
              <div className="border border-[#262626] bg-[#141414]">
                <div className="p-4 border-b border-[#262626]">
                  <h2 className="text-base font-semibold text-[#FAFAFA]">Шаблоны банковских выписок</h2>
                  <p className="text-xs text-[#525252] mt-1">Загрузите PDF шаблоны выписок (Фунты, Евро)</p>
                </div>
                <div className="p-4">
                  <TemplateSection
                    templates={statementTemplates}
                    loading={loadingStatementTemplates}
                    uploading={uploadingStatement}
                    templateName={statementTemplateName}
                    setTemplateName={setStatementTemplateName}
                    endpoint="statement-templates"
                    setTemplates={setStatementTemplates}
                    setUploading={setUploadingStatement}
                    uploadInputId="statement-template"
                    onUpload={uploadTemplate}
                    onDelete={deleteTemplate}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add Manager Dialog */}
      <Dialog open={showAddManager} onOpenChange={setShowAddManager}>
        <DialogContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA]">Добавить менеджера</DialogTitle>
            <DialogDescription className="text-[#525252]">Создайте аккаунт для нового менеджера</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Имя</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
                <Input
                  value={managerForm.name}
                  onChange={(e) => setManagerForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Иван Иванов"
                  className="pl-10 bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-10 focus:border-[#FACC15] focus:ring-0"
                  data-testid="manager-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Email</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
                <Input
                  type="email"
                  value={managerForm.email}
                  onChange={(e) => setManagerForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="manager@docflow.com"
                  className="pl-10 bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-10 focus:border-[#FACC15] focus:ring-0"
                  data-testid="manager-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Пароль</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
                <Input
                  type="password"
                  value={managerForm.password}
                  onChange={(e) => setManagerForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Минимум 6 символов"
                  className="pl-10 bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-10 focus:border-[#FACC15] focus:ring-0"
                  data-testid="manager-password-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowAddManager(false)}
              variant="ghost"
              className="text-[#A3A3A3] hover:text-[#FAFAFA] hover:bg-[#262626] rounded-none"
            >
              Отмена
            </Button>
            <Button
              onClick={handleAddManager}
              disabled={addingManager}
              className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none"
              data-testid="confirm-add-manager"
            >
              {addingManager ? <SpinnerGap size={16} className="animate-spin mr-2" /> : null}
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TOTP Secret Dialog — shown after creating a manager */}
      <Dialog open={!!newManagerTotp} onOpenChange={() => setNewManagerTotp(null)}>
        <DialogContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
          <DialogHeader>
            <DialogTitle className="text-[#FACC15]">2FA Ключ для менеджера</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              Скопируйте этот ключ и отправьте менеджеру для настройки Google Authenticator
            </DialogDescription>
          </DialogHeader>
          {newManagerTotp && (
            <div className="space-y-4 py-2">
              <div className="p-4 border border-[#262626] bg-[#0A0A0A]">
                <p className="text-xs text-[#525252] mb-1">Имя</p>
                <p className="text-sm text-[#FAFAFA]">{newManagerTotp.name}</p>
              </div>
              <div className="p-4 border border-[#262626] bg-[#0A0A0A]">
                <p className="text-xs text-[#525252] mb-1">Email</p>
                <p className="text-sm text-[#FAFAFA]">{newManagerTotp.email}</p>
              </div>
              <div className="p-4 border border-[#FACC15]/30 bg-[#FACC15]/5">
                <p className="text-xs text-[#FACC15] mb-2 font-semibold uppercase tracking-wider">Секретный ключ 2FA (Google Authenticator)</p>
                <p className="text-lg font-mono select-all text-[#FAFAFA] tracking-wider break-all" data-testid="manager-totp-secret">
                  {newManagerTotp.secret}
                </p>
              </div>
              <p className="text-xs text-[#525252]">
                Менеджер должен добавить этот ключ в Google Authenticator вручную (ввести ключ настройки).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                if (newManagerTotp?.secret) {
                  navigator.clipboard.writeText(newManagerTotp.secret);
                  showSuccess('Ключ скопирован!');
                }
              }}
              variant="outline"
              className="border-[#FACC15] text-[#FACC15] hover:bg-[#FACC15]/10 rounded-none"
              data-testid="copy-totp-btn"
            >
              Копировать ключ
            </Button>
            <Button
              onClick={() => setNewManagerTotp(null)}
              className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none"
            >
              Готово
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
